/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {Node} from 'prosemirror-model';
import {NodeView} from 'prosemirror-view';
import {CellAttrs} from './util';

/**
 * @public
 */
export class TableView implements NodeView {
    /**
     * Dom.
     */
    public dom: HTMLDivElement;

    /**
     * Table.
     */
    public table: HTMLTableElement;

    /**
     * Column Group.
     */
    public colgroup: HTMLTableColElement;

    /**
     * Content DOM.
     */
    public contentDOM: HTMLTableSectionElement;

    /**
     * Default constructor.
     * 
     * @param node Node.
     * @param cellMinWidth Cell minWidth.
     */
    constructor(public node: Node, public cellMinWidth: number) {
        this.dom = document.createElement('div');
        this.dom.className = 'tableWrapper';
        this.table = this.dom.appendChild(document.createElement('table'));
        this.colgroup = this.table.appendChild(document.createElement('colgroup'));
        updateColumnsOnResize(node, this.colgroup, this.table, cellMinWidth);
        this.contentDOM = this.table.appendChild(document.createElement('tbody'));
    }

    /**
     * Update function.
     * 
     * @param node Node.
     * @returns Boolean.
     */
    update(node: Node): boolean {
        if (node.type != this.node.type) return false;
        this.node = node;
        updateColumnsOnResize(node, this.colgroup, this.table, this.cellMinWidth);
        return true;
    }

    /**
     * Ignore mutation.
     * 
     * @param record Record.
     * @returns Boolean.
     */
    ignoreMutation(record: any): boolean {
        return (
            record.type == 'attributes' &&
            (record.target == this.table || this.colgroup.contains(record.target))
        );
    }
}

/**
 * Update columns on resize.
 * 
 * @param node Node.
 * @param colgroup Column group.
 * @param table Table.
 * @param cellMinWidth Cell minWidth.
 * @param overrideCol Override column.
 * @param overrideValue Override value.
 * @public
 */
export function updateColumnsOnResize(
    node: Node,
    colgroup: HTMLTableColElement,
    table: HTMLTableElement,
    cellMinWidth: number,
    overrideCol?: number,
    overrideValue?: number
): void {
    let totalWidth = 0;
    let fixedWidth = true;
    let nextDOM = colgroup.firstChild as HTMLElement;
    const row = node.firstChild;
    if (!row) return;

    for (let i = 0, col = 0; i < row.childCount; i++) {
        const {colspan, colwidth} = row.child(i).attrs as CellAttrs;
        for (let j = 0; j < colspan; j++, col++) {
            const hasWidth =
                overrideCol == col ? overrideValue : colwidth && colwidth[j];
            const cssWidth = hasWidth ? hasWidth + 'px' : '';
            totalWidth += hasWidth || cellMinWidth;
            if (!hasWidth) fixedWidth = false;
            if (!nextDOM) {
                colgroup.appendChild(document.createElement('col')).style.width =
                    cssWidth;
            } else {
                if (nextDOM.style.width != cssWidth) nextDOM.style.width = cssWidth;
                nextDOM = nextDOM.nextSibling as HTMLElement;
            }
        }
    }

    while (nextDOM) {
        const after = nextDOM.nextSibling;
        nextDOM.parentNode?.removeChild(nextDOM);
        nextDOM = after as HTMLElement;
    }

    if (fixedWidth) {
        table.style.width = totalWidth + 'px';
        table.style.minWidth = '';
    } else {
        table.style.width = '';
        table.style.minWidth = totalWidth + 'px';
    }
}
